import { usePublicActiveRoles, RoleBadge } from "@/lib/roles";
import { formatPlace, districtsFor } from "@/lib/districts";
import { GrowthReminder } from "@/components/NextStep";
import { SectionHint } from "@/components/SectionHint";
import { ExpertVenueCard } from "@/components/ExpertVenueCard";
import { ALL_TURKEY_LABEL, categoryFilterOptions } from "@/lib/filter-options";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  PageHeader,
  SearchFilterRow,
  SegmentedTabs,
  ActiveFilters,
  IconToggleButton,
  SectionTitle,
} from "@/components/kit";
import { profileCoreProgress } from "@/lib/profile-progress";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { pluralMeetings } from "@/lib/plural";
import { useMemo, useState, useEffect, useRef } from "react";
import {
  MapPin,
  Briefcase,
  Target,
  Coffee,
  Heart,
  Globe,
  ChevronDown,
  ChevronUp,
  Bookmark,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ResidentProfileView } from "@/components/ResidentProfileView";
import { PickedForYouSection } from "@/components/PickedForYouSection";
import { PersonCard, type OrgStat } from "@/components/PersonCard";
import { HostedCirclesProvider } from "@/lib/hosted-circles";

import { useAuth } from "@/lib/auth";
import {
  INDUSTRY_OPTIONS,
  TURKEY_CITIES,
  COFFEE_STATUS_OPTIONS,
  COFFEE_STATUS_EXCLUSIVE,
  INTEREST_OPTIONS,
  LANGUAGE_OPTIONS,
  canInviteByStatus,
  profileIndustries,
  isTourist,
  VISITOR_KIND_OPTIONS,
  type Profile,
} from "@/lib/people-constants";
import { InviteToCoffeeDialog } from "@/components/InviteToCoffeeDialog";
import { useSavedContacts } from "@/lib/saved-contacts";
import { fallbackPeopleFor, fetchPeople, rankMatches, sharedCount } from "@/lib/match-people";
import { countByCategory, filterByCategory, toggleCategory } from "@/lib/expert-category-filter";
import {
  EXPERT_CATEGORIES,
  expertCategoriesOf,
  isExpert,
  primaryContact,
  bloggerLink,
  logExpertContact,
} from "@/lib/expert";
import { Button } from "@/components/ui/button";
import { LOOKING_FOR_GROUPS } from "@/lib/profile-purpose";

const SHOWCASE_STEP = 3;
const SHOWCASE_MAX = 9;

export const Route = createFileRoute("/_authenticated/people")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { saved?: boolean; experts?: boolean; organizers?: boolean; category?: string } => ({
    saved: search["saved"] === true || search["saved"] === "true" ? true : undefined,
    experts:
      search["experts"] === true ||
      search["experts"] === 1 ||
      search["experts"] === "1" ||
      search["experts"] === "true"
        ? true
        : undefined,
    organizers:
      search["organizers"] === true ||
      search["organizers"] === 1 ||
      search["organizers"] === "1" ||
      search["organizers"] === "true"
        ? true
        : undefined,
    category:
      typeof search["category"] === "string" && search["category"] ? search["category"] : undefined,
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["people"],
      queryFn: fetchPeople,
    });
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-white px-4 py-12">
      <div className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold text-red-600">Не удалось загрузить участников</h1>
        <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-white px-4 py-12 text-center">Ничего не найдено.</div>
  ),
  pendingComponent: () => (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-[720px] px-5 py-8">
        <div className="h-8 w-64 animate-pulse rounded bg-td-soft" />
      </main>
    </div>
  ),
  component: PeoplePage,
});

const OTHER_CITY = "__other__";
type ViewMode = "all" | "experts" | "organizers" | "saved" | "bloggers";
function PeoplePage() {
  const { user } = useAuth();
  const { data: people } = useSuspenseQuery({
    queryKey: ["people"],
    queryFn: fetchPeople,
  });

  const filled = useMemo(
    () => people.filter((p) => p.full_name?.trim() && p.city?.trim()),
    [people],
  );

  const me = filled.find((p) => p.user_id === user?.id);
  const myInterests = new Set((me?.interests ?? []).map((s) => s.toLowerCase()));

  // Track first visit to /people (fire-and-forget)
  useEffect(() => {
    if (!user?.id) return;
    if (me && (me as unknown as { has_visited_people?: boolean }).has_visited_people) return;
    supabase
      .from("profiles")
      .update({ has_visited_people: true })
      .eq("user_id", user.id)
      .then(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, me?.user_id]);

  const {
    saved: savedFromUrl,
    experts: expertsFromUrl,
    organizers: organizersFromUrl,
    category: categoryFromUrl,
  } = Route.useSearch();
  const initialMode = (): ViewMode =>
    savedFromUrl
      ? "saved"
      : organizersFromUrl
        ? "organizers"
        : expertsFromUrl || categoryFromUrl
          ? "experts"
          : "all";
  const { savedIds } = useSavedContacts();
  const [viewMode, setViewMode] = useState<ViewMode>(initialMode);
  const { data: orgEvents = [] } = useQuery({
    queryKey: ["people-organizers"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("organizer_user_id, co_organizer_user_id, event_date")
        .eq("status", "published")
        .limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });
  const orgStats = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, OrgStat>();
    for (const e of orgEvents) {
      const t = e.event_date ? new Date(e.event_date).getTime() : NaN;
      for (const id of new Set(
        [e.organizer_user_id, e.co_organizer_user_id].filter((x): x is string => !!x),
      )) {
        const cur = map.get(id) ?? { count: 0, next: null };
        if (Number.isFinite(t) && t >= now) {
          if (cur.next === null || t < cur.next) cur.next = t;
        } else cur.count += 1;
        map.set(id, cur);
      }
    }
    return map;
  }, [orgEvents]);
  const bloggersCount = useMemo(() => filled.filter((p) => !!bloggerLink(p)).length, [filled]);
  const showBloggers = bloggersCount >= 3;
  const [query, setQuery] = useState("");
  const [visibleMatches, setVisibleMatches] = useState(SHOWCASE_STEP);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [invitePerson, setInvitePerson] = useState<Profile | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Profile | null>(null);

  // Committed filters (applied)
  const [cities, setCities] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [draftDistricts, setDraftDistricts] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [lookingFor, setLookingFor] = useState<string[]>([]);
  const [coffeeStatuses, setCoffeeStatuses] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [visitorKinds, setVisitorKinds] = useState<string[]>([]);
  const [expertCats, setExpertCats] = useState<string[]>(categoryFromUrl ? [categoryFromUrl] : []);
  const navigatePeople = useNavigate();
  // Режим, выбранный вкладкой: адрес меняется (?experts=1 / без него), но вкладка остаётся выбранной.
  const pendingMode = useRef<ViewMode | null>(null);
  const selectMode = (m: ViewMode) => {
    const urlExperts = !!(expertsFromUrl || categoryFromUrl);
    if (m !== "all") setExpertCats([]);
    if (m === "experts" && !urlExperts) {
      pendingMode.current = "experts";
      void navigatePeople({ to: "/people", search: { experts: true }, replace: true });
    } else if (m !== "experts" && (urlExperts || savedFromUrl || organizersFromUrl)) {
      pendingMode.current = m;
      void navigatePeople({ to: "/people", search: {}, replace: true });
    }
    setViewMode(m);
  };
  useEffect(() => {
    if (pendingMode.current) {
      setViewMode(pendingMode.current);
      pendingMode.current = null;
      return;
    }
    setViewMode(initialMode());
    setExpertCats(categoryFromUrl ? [categoryFromUrl] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFromUrl, expertsFromUrl, organizersFromUrl, categoryFromUrl]);

  // Draft filters (inside drawer, applied on submit)
  const [draftCities, setDraftCities] = useState<string[]>([]);
  const [draftIndustries, setDraftIndustries] = useState<string[]>([]);
  const [draftLookingFor, setDraftLookingFor] = useState<string[]>([]);
  const [draftCoffeeStatuses, setDraftCoffeeStatuses] = useState<string[]>([]);
  const [draftInterests, setDraftInterests] = useState<string[]>([]);
  const [draftLanguages, setDraftLanguages] = useState<string[]>([]);
  const [draftVisitorKinds, setDraftVisitorKinds] = useState<string[]>([]);

  const knownCitySet = useMemo(() => new Set<string>(TURKEY_CITIES as readonly string[]), []);

  const [draftCat, setDraftCat] = useState("");
  function openDrawer() {
    setDraftCat(expertCats[0] ?? "");
    setDraftCities(cities);
    setDraftDistricts(districts);
    setDraftIndustries(industries);
    setDraftLookingFor(lookingFor);
    setDraftCoffeeStatuses(coffeeStatuses);
    setDraftInterests(interests);
    setDraftLanguages(languages);
    setDraftVisitorKinds(visitorKinds);
    setDrawerOpen(true);
  }
  function applyDraft() {
    setCities(draftCities);
    setDistricts(draftCities.length ? draftDistricts : []);
    setIndustries(draftIndustries);
    setLookingFor(draftLookingFor);
    setCoffeeStatuses(draftCoffeeStatuses);
    setInterests(draftInterests);
    setLanguages(draftLanguages);
    setVisitorKinds(draftVisitorKinds);
    if (viewMode === "experts" && draftCat !== (expertCats[0] ?? "")) setCategory(draftCat || null);
    setDrawerOpen(false);
  }
  function resetDraft() {
    setDraftCat("");
    setDraftCities([]);
    setDraftDistricts([]);
    setDraftIndustries([]);
    setDraftLookingFor([]);
    setDraftCoffeeStatuses([]);
    setDraftInterests([]);
    setDraftLanguages([]);
    setDraftVisitorKinds([]);
  }

  const activeFilterCount =
    cities.length +
    districts.length +
    industries.length +
    lookingFor.length +
    coffeeStatuses.length +
    interests.length +
    languages.length +
    visitorKinds.length;

  const draftDistrictOptions = useMemo(() => {
    const picked = draftCities.filter((c) => c !== OTHER_CITY);
    if (!picked.length) return [];
    const set = new Set<string>();
    for (const c of picked) for (const d of districtsFor(c)) set.add(d.name);
    for (const p of filled)
      if (picked.includes(p.city) && p.district?.trim()) set.add(p.district.trim());
    return [...set];
  }, [draftCities, filled]);

  const progress = useMemo(() => profileCoreProgress(me), [me]);
  const profileReady = progress.done === progress.total;

  const sortedMatches = useMemo(
    () => rankMatches(filled, user?.id, myInterests, me?.city),
    [filled, user?.id, myInterests, me?.city],
  );

  const fallbackPeople = useMemo(
    () => fallbackPeopleFor(filled, user?.id, me?.city),
    [filled, user?.id, me?.city],
  );

  const showcasePool = useMemo(() => {
    if (profileReady) return sortedMatches;
    if (progress.done >= 3) return sortedMatches.length > 0 ? sortedMatches : fallbackPeople;
    return fallbackPeople;
  }, [profileReady, progress.done, sortedMatches, fallbackPeople]);

  const isMatchesPool = showcasePool === sortedMatches;
  const showcaseLimit = isMatchesPool
    ? Math.min(visibleMatches, SHOWCASE_MAX)
    : progress.done >= 3
      ? 3
      : 2;

  const showcase = useMemo(
    () => showcasePool.slice(0, showcaseLimit),
    [showcasePool, showcaseLimit],
  );

  const showcaseTitle = profileReady
    ? "Подобрал для тебя"
    : progress.done >= 3
      ? sortedMatches.length > 0
        ? "Рекомендации по интересам"
        : "Люди из твоего города"
      : me?.city?.trim()
        ? "Люди из твоего города"
        : "Новые участники рядом";

  const canShowMore =
    isMatchesPool && showcase.length < showcasePool.length && showcase.length < SHOWCASE_MAX;
  const canCollapse = isMatchesPool && showcase.length > SHOWCASE_STEP;

  const recommended = showcase;

  const recommendedIds = useMemo(() => new Set(recommended.map((p) => p.user_id)), [recommended]);

  const preCategory = useMemo(() => {
    const q = query.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    const list = filled.filter((p) => {
      if (viewMode === "saved" && !savedIds.has(p.user_id)) return false;
      // При активном поиске показываем плоский список всех совпавших,
      // включая тех, кто попал в блок «Подобрал для тебя».
      if (viewMode === "all" && !q && recommendedIds.has(p.user_id)) return false;
      if (viewMode === "experts" && !isExpert(p)) return false;
      if (viewMode === "organizers" && !orgStats.has(p.user_id)) return false;
      if (viewMode === "bloggers" && !bloggerLink(p)) return false;
      if (cities.length) {
        const inOther = cities.includes(OTHER_CITY) && !knownCitySet.has(p.city);
        if (!inOther && !cities.includes(p.city)) return false;
      }
      if (districts.length && !districts.includes((p.district ?? "").trim())) return false;
      if (industries.length) {
        const pInds = profileIndustries(p);
        if (!industries.some((v) => pInds.includes(v))) return false;
      }
      if (lookingFor.length) {
        const set = new Set(p.looking_for_tags ?? []);
        if (
          !lookingFor.some((label) =>
            LOOKING_FOR_GROUPS.find((group) => group.label === label)?.tags.some((tag) =>
              set.has(tag),
            ),
          )
        )
          return false;
      }
      if (coffeeStatuses.length) {
        const arr = Array.isArray(p.coffee_status)
          ? p.coffee_status
          : p.coffee_status
            ? [p.coffee_status as unknown as string]
            : [];
        if (!coffeeStatuses.some((s) => arr.includes(s))) return false;
      }
      if (interests.length) {
        const set = new Set((p.interests ?? []).map((s) => s.toLowerCase()));
        if (!interests.some((i) => set.has(i.toLowerCase()))) return false;
      }
      if (languages.length) {
        const set = new Set(p.languages ?? []);
        if (!languages.some((l) => set.has(l))) return false;
      }
      if (visitorKinds.length) {
        if (!visitorKinds.includes(p.visitor_kind ?? "resident")) return false;
      }
      if (q) {
        const hay = [
          p.full_name ?? "",
          p.username ?? "",
          p.profession ?? "",
          ...profileIndustries(p),
          ...(p.role_tags ?? []),
          p.can_help_with ?? "",
          p.expert_offer ?? "",
          ...expertCategoriesOf(p),
          ...(p.looking_for_tags ?? []),
          p.conversation_topic ?? "",
          p.about ?? "",
          ...(p.interests ?? []),
          ...(p.languages ?? []),
          p.city ?? "",
          p.origin_city ?? "",
          p.time_in_turkey ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .replace(/ё/g, "е");
        if (!tokens.every((t) => hay.includes(t.replace(/ё/g, "е")))) return false;
      }
      return true;
    });
    if (viewMode === "organizers") {
      const key = (p: Profile) => orgStats.get(p.user_id) ?? { count: 0, next: null };
      return [...list].sort((a, b) => {
        // Сначала те, кто организует мероприятия для экспертов.
        if (!!a.hosts_for_experts !== !!b.hosts_for_experts) return a.hosts_for_experts ? -1 : 1;
        const ka = key(a),
          kb = key(b);
        if ((ka.next !== null) !== (kb.next !== null)) return ka.next !== null ? -1 : 1;
        if (ka.next !== null && kb.next !== null && ka.next !== kb.next) return ka.next - kb.next;
        return kb.count - ka.count;
      });
    }
    return list
      .map((p, idx) => ({ p, idx, s: sharedCount(p, myInterests) }))
      .sort((a, b) => b.s - a.s || a.idx - b.idx)
      .map((x) => x.p);
  }, [
    filled,
    query,
    cities,
    districts,
    industries,
    lookingFor,
    coffeeStatuses,
    interests,
    languages,
    visitorKinds,
    recommendedIds,
    knownCitySet,
    myInterests,
    viewMode,
    savedIds,
    orgStats,
  ]);

  const expertCat = viewMode === "experts" ? (expertCats[0] ?? null) : null;
  const filtered = useMemo(
    () => filterByCategory(preCategory, expertCat, expertCategoriesOf),
    [preCategory, expertCat],
  );
  const catCounts = useMemo(
    () =>
      countByCategory(
        viewMode === "experts" ? preCategory : [],
        EXPERT_CATEGORIES,
        expertCategoriesOf,
      ),
    [preCategory, viewMode],
  );
  const setCategory = (next: string | null) => {
    setExpertCats(next ? [next] : []);
    pendingMode.current = "experts";
    void navigatePeople({
      to: "/people",
      search: next ? { experts: true, category: next } : { experts: true },
      replace: true,
    });
  };
  const pickCategory = (c: string | null) => {
    const next = toggleCategory(expertCat, c);
    setExpertCats(next ? [next] : []);
    pendingMode.current = "experts";
    void navigatePeople({
      to: "/people",
      search: next ? { experts: true, category: next } : { experts: true },
      replace: true,
    });
  };

  const isExpertsSection =
    viewMode === "experts" || viewMode === "organizers" || viewMode === "bloggers";

  const removeFrom = (set: React.Dispatch<React.SetStateAction<string[]>>, v: string) => () =>
    set((prev) => prev.filter((x) => x !== v));
  const activeChips = [
    ...(expertCat ? [{ key: "cat", label: expertCat, onRemove: () => setCategory(null) }] : []),
    ...cities.map((c) => ({
      key: `c-${c}`,
      label: c === OTHER_CITY ? "Другой город" : c,
      onRemove: removeFrom(setCities, c),
    })),
    ...districts.map((v) => ({ key: `d-${v}`, label: v, onRemove: removeFrom(setDistricts, v) })),
    ...visitorKinds.map((v) => ({
      key: `v-${v}`,
      label: VISITOR_KIND_OPTIONS.find((o) => o.value === v)?.label ?? v,
      onRemove: removeFrom(setVisitorKinds, v),
    })),
    ...industries.map((v) => ({ key: `i-${v}`, label: v, onRemove: removeFrom(setIndustries, v) })),
    ...interests.map((v) => ({ key: `n-${v}`, label: v, onRemove: removeFrom(setInterests, v) })),
    ...lookingFor.map((v) => ({ key: `l-${v}`, label: v, onRemove: removeFrom(setLookingFor, v) })),
    ...coffeeStatuses.map((v) => ({
      key: `s-${v}`,
      label: COFFEE_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v,
      onRemove: removeFrom(setCoffeeStatuses, v),
    })),
    ...languages.map((v) => ({ key: `g-${v}`, label: v, onRemove: removeFrom(setLanguages, v) })),
  ];
  function resetAllFilters() {
    setCities([]);
    setDistricts([]);
    setIndustries([]);
    setLookingFor([]);
    setCoffeeStatuses([]);
    setInterests([]);
    setLanguages([]);
    setVisitorKinds([]);
    if (expertCat) setCategory(null);
  }

  return (
    <div className="min-h-screen min-w-0 max-w-full overflow-x-clip bg-white">
      <HostedCirclesProvider userIds={[...recommended, ...filtered].map((x) => x.user_id)}>
        <main className="mx-auto min-w-0 max-w-[720px] px-4 py-6 md:py-10">
          <PageHeader
            title={isExpertsSection ? "Эксперты" : "Находи своих"}
            action={
              viewMode === "experts" ? (
                <Link
                  to="/profile"
                  search={{ section: "services" }}
                  className="inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-full border border-td-teal bg-white px-4 text-sm font-semibold text-td-teal hover:bg-td-teal/10"
                >
                  {me && isExpert(me) ? "Моя карточка" : "Стать экспертом"}
                </Link>
              ) : undefined
            }
          />
          <SectionHint
            key={viewMode === "experts" ? "experts" : "people"}
            hint={viewMode === "experts" ? "experts" : "people"}
            className="mt-4"
          />
          <GrowthReminder className="mt-4" />

          <div className="sticky top-0 z-10 -mx-4 bg-white/95 px-4 py-3 backdrop-blur-sm">
            <SearchFilterRow
              value={query}
              onChange={setQuery}
              placeholder="Имя, профессия, интересы"
              filterCount={activeFilterCount + (expertCat ? 1 : 0)}
              filtersTitle="Фильтры"
              filtersOpen={drawerOpen}
              onFiltersOpenChange={(o) => (o ? openDrawer() : setDrawerOpen(false))}
              onReset={resetDraft}
              onApply={applyDraft}
              trailing={
                <IconToggleButton
                  label="Сохранённые"
                  active={viewMode === "saved"}
                  onClick={() => selectMode(viewMode === "saved" ? "all" : "saved")}
                >
                  <Bookmark className="size-4" />
                </IconToggleButton>
              }
              sections={
                viewMode === "experts"
                  ? [
                      {
                        key: "category",
                        title: "Направление",
                        mode: "single",
                        options: categoryFilterOptions(EXPERT_CATEGORIES, catCounts.byCategory),
                        value: draftCat,
                        onChange: setDraftCat,
                      },
                    ]
                  : undefined
              }
              filters={
                <div>
                  <FilterGroup icon={<MapPin className="h-4 w-4" />} label="Город">
                    <PillToggle
                      active={draftCities.length === 0}
                      onClick={() => setDraftCities([])}
                    >
                      {ALL_TURKEY_LABEL}
                    </PillToggle>
                    {TURKEY_CITIES.map((c) => (
                      <PillToggle
                        key={c}
                        active={draftCities.includes(c)}
                        onClick={() =>
                          setDraftCities((prev) =>
                            prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c],
                          )
                        }
                      >
                        {c}
                      </PillToggle>
                    ))}
                    <PillToggle
                      active={draftCities.includes(OTHER_CITY)}
                      onClick={() =>
                        setDraftCities((prev) =>
                          prev.includes(OTHER_CITY)
                            ? prev.filter((v) => v !== OTHER_CITY)
                            : [...prev, OTHER_CITY],
                        )
                      }
                    >
                      Другое
                    </PillToggle>
                  </FilterGroup>

                  {draftDistrictOptions.length > 0 && (
                    <FilterGroup icon={<MapPin className="h-4 w-4" />} label="Район">
                      {draftDistrictOptions.map((d) => (
                        <PillToggle
                          key={d}
                          active={draftDistricts.includes(d)}
                          onClick={() =>
                            setDraftDistricts((prev) =>
                              prev.includes(d) ? prev.filter((v) => v !== d) : [...prev, d],
                            )
                          }
                        >
                          {d}
                        </PillToggle>
                      ))}
                    </FilterGroup>
                  )}

                  <FilterGroup icon={<MapPin className="h-4 w-4" />} label="Тип пребывания">
                    {VISITOR_KIND_OPTIONS.map((opt) => (
                      <PillToggle
                        key={opt.value}
                        active={draftVisitorKinds.includes(opt.value)}
                        onClick={() =>
                          setDraftVisitorKinds((prev) =>
                            prev.includes(opt.value)
                              ? prev.filter((v) => v !== opt.value)
                              : [...prev, opt.value],
                          )
                        }
                      >
                        {opt.label}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup icon={<Briefcase className="h-4 w-4" />} label="Сфера деятельности">
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <PillToggle
                        key={opt}
                        active={draftIndustries.includes(opt)}
                        onClick={() =>
                          setDraftIndustries((prev) =>
                            prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt],
                          )
                        }
                      >
                        {opt}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup icon={<Heart className="h-4 w-4" />} label="Интересы">
                    {INTEREST_OPTIONS.map((opt) => (
                      <PillToggle
                        key={opt}
                        active={draftInterests.includes(opt)}
                        onClick={() =>
                          setDraftInterests((prev) =>
                            prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt],
                          )
                        }
                      >
                        {opt}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup icon={<Target className="h-4 w-4" />} label="Что ищет">
                    {LOOKING_FOR_GROUPS.map((group) => (
                      <PillToggle
                        key={group.label}
                        active={draftLookingFor.includes(group.label)}
                        onClick={() =>
                          setDraftLookingFor((prev) =>
                            prev.includes(group.label)
                              ? prev.filter((v) => v !== group.label)
                              : [...prev, group.label],
                          )
                        }
                      >
                        {group.label}
                      </PillToggle>
                    ))}
                  </FilterGroup>

                  <FilterGroup icon={<Coffee className="h-4 w-4" />} label="Готовность к контакту">
                    {COFFEE_STATUS_OPTIONS.filter((o) => o.value !== COFFEE_STATUS_EXCLUSIVE).map(
                      (opt) => (
                        <PillToggle
                          key={opt.value}
                          active={draftCoffeeStatuses.includes(opt.value)}
                          onClick={() =>
                            setDraftCoffeeStatuses((prev) =>
                              prev.includes(opt.value)
                                ? prev.filter((v) => v !== opt.value)
                                : [...prev, opt.value],
                            )
                          }
                        >
                          {opt.label}
                        </PillToggle>
                      ),
                    )}
                  </FilterGroup>

                  <FilterGroup icon={<Globe className="h-4 w-4" />} label="Языки">
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <PillToggle
                        key={opt}
                        active={draftLanguages.includes(opt)}
                        onClick={() =>
                          setDraftLanguages((prev) =>
                            prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt],
                          )
                        }
                      >
                        {opt}
                      </PillToggle>
                    ))}
                  </FilterGroup>
                </div>
              }
            />
            <div className="mt-3">
              <SegmentedTabs<ViewMode>
                value={viewMode === "saved" ? ("" as ViewMode) : viewMode}
                onChange={(k) => {
                  if (k === "all") setExpertCats([]);
                  selectMode(k);
                }}
                items={[
                  { key: "all", label: "Все" },
                  { key: "experts", label: "Эксперты" },
                  { key: "organizers", label: "Организаторы" },
                  ...(showBloggers ? [{ key: "bloggers" as ViewMode, label: "Блогеры" }] : []),
                ]}
              />
            </div>
          </div>

          <ActiveFilters items={activeChips} onReset={resetAllFilters} />

          {viewMode === "organizers" && <ExpertVenueCard />}

          {viewMode === "organizers" && (
            <p className="mt-5 break-words text-sm leading-relaxed text-td-muted">
              Резиденты, которые собирают людей на встречи. Хочешь сюда?{" "}
              <Link
                to="/events/new"
                className="font-medium text-td-teal underline underline-offset-2"
              >
                Создай своё мероприятие в Афише
              </Link>
            </p>
          )}
          {viewMode === "bloggers" && (
            <p className="mt-5 break-words text-sm leading-relaxed text-td-muted">
              Резиденты, которые ведут блоги и каналы. Ты тоже?{" "}
              <Link
                to="/profile"
                search={{ section: "contacts" }}
                className="font-medium text-td-teal underline underline-offset-2"
              >
                Отметь «Я блогер» в профиле
              </Link>
            </p>
          )}

          {viewMode === "all" && !profileReady && (
            <section className="mt-6 rounded-2xl border border-td-teal/25 bg-td-teal/5 p-4">
              {progress.done < 3 ? (
                <>
                  <h2 className="flex items-center gap-1.5 text-sm font-semibold text-td-dark">
                    <span aria-hidden="true">🐾</span> Помоги MrDrive найти твоих
                  </h2>
                  <p className="mt-1.5 text-sm text-td-muted">
                    Расскажи немного о себе — и я подберу людей по интересам, целям и тому, что
                    важно именно тебе.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="flex items-center gap-1.5 text-sm font-semibold text-td-dark">
                    <span aria-hidden="true">🐾</span> Основа профиля: {progress.done} из{" "}
                    {progress.total}
                  </h2>
                  <p className="mt-1.5 text-sm text-td-muted">
                    Добавь {progress.missing[0]?.label}, и подбор станет точнее.
                  </p>
                </>
              )}
              <Link
                to="/profile"
                search={{
                  section:
                    progress.missing[0]?.key === "interests"
                      ? "interests"
                      : progress.missing[0]?.key === "contact"
                        ? "contacts"
                        : "basic",
                }}
                className="mt-3 inline-flex items-center rounded-full bg-td-teal px-4 py-2 text-xs font-semibold text-white transition hover:bg-td-teal/90"
              >
                {progress.done < 3 ? "Заполнить профиль →" : "Дополнить профиль →"}
              </Link>
              <p className="mt-2.5 text-[11px] text-td-muted/90">
                Чем полнее профиль — тем чаще тебя показывают другим.
              </p>
            </section>
          )}

          {viewMode === "all" && !query.trim() && recommended.length > 0 && (
            <PickedForYouSection
              className="mt-4"
              title={showcaseTitle}
              showPaw={profileReady}
              after={
                <>
                  {(canShowMore || canCollapse) && (
                    <div className="mt-3 flex gap-2">
                      {canShowMore && (
                        <button
                          type="button"
                          onClick={() =>
                            setVisibleMatches((v) => Math.min(v + SHOWCASE_STEP, SHOWCASE_MAX))
                          }
                          className="flex w-full items-center justify-center gap-1 rounded-xl border border-td-teal/30 bg-white py-2 text-sm font-medium text-td-teal transition hover:bg-td-teal/5"
                        >
                          Показать ещё <ChevronDown className="h-4 w-4" />
                        </button>
                      )}
                      {canCollapse && (
                        <button
                          type="button"
                          onClick={() => setVisibleMatches(SHOWCASE_STEP)}
                          className="flex w-full items-center justify-center gap-1 rounded-xl border border-td-teal/30 bg-white py-2 text-sm font-medium text-td-teal transition hover:bg-td-teal/5"
                        >
                          Свернуть <ChevronUp className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                  {isMatchesPool &&
                    showcasePool.length > SHOWCASE_MAX &&
                    showcase.length === SHOWCASE_MAX && (
                      <p className="mt-2 text-center text-xs text-td-muted">
                        Остальные — ниже, в списке резидентов
                      </p>
                    )}
                </>
              }
            >
              {recommended.map((p) => (
                <PersonCard
                  key={p.user_id}
                  p={p}
                  myInterests={myInterests}
                  showSharedNames={profileReady}
                  onInvite={() => setInvitePerson(p)}
                  onOpen={() => setSelectedPerson(p)}
                />
              ))}
            </PickedForYouSection>
          )}

          <SectionTitle
            className="mt-6 mb-3"
            aside={`${viewMode !== "all" || query.trim() ? filtered.length : filtered.length + recommended.length} чел.`}
          >
            {viewMode === "saved"
              ? "Сохранённые контакты"
              : viewMode === "experts"
                ? "Эксперты"
                : viewMode === "organizers"
                  ? "Организаторы"
                  : viewMode === "bloggers"
                    ? "Блогеры"
                    : "Резиденты"}
          </SectionTitle>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-td-line bg-white p-8 text-center text-td-muted">
              {viewMode === "saved" ? (
                "🔖 Здесь появятся контакты, которые ты сохранишь. Это видно только тебе."
              ) : viewMode === "experts" && expertCats.length > 0 ? (
                <>
                  <p>В этом направлении пока нет экспертов</p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 h-11 rounded-full border-td-teal text-td-teal hover:bg-td-teal/10 hover:text-td-teal"
                    onClick={() => pickCategory(null)}
                  >
                    Показать всех экспертов
                  </Button>
                </>
              ) : viewMode === "organizers" ? (
                <p>
                  Организаторов пока нет. Стань первым!{" "}
                  <Link
                    to="/events/new"
                    className="font-medium text-td-teal underline underline-offset-2"
                  >
                    Создай своё мероприятие в Афише
                  </Link>
                </p>
              ) : viewMode === "experts" ? (
                <p>
                  Экспертов пока нет — стань первым!{" "}
                  <Link
                    to="/profile"
                    search={{ section: "services" }}
                    className="font-medium text-td-teal underline underline-offset-2"
                  >
                    Заполни «Мои услуги» в профиле
                  </Link>
                </p>
              ) : (
                "🐾 Здесь пока никого не нашлось. Попробуй изменить фильтры."
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((p) => (
                <PersonCard
                  key={p.user_id}
                  p={p}
                  myInterests={myInterests}
                  onInvite={() => setInvitePerson(p)}
                  onOpen={() => setSelectedPerson(p)}
                  selectedCats={viewMode === "experts" ? expertCats : undefined}
                  mode={viewMode}
                  orgStat={orgStats.get(p.user_id)}
                />
              ))}
            </div>
          )}
        </main>
      </HostedCirclesProvider>

      <Dialog open={!!selectedPerson} onOpenChange={(o) => !o && setSelectedPerson(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg p-0">
          {selectedPerson && (
            <>
              <DialogTitle className="sr-only">{selectedPerson.full_name}</DialogTitle>
              <ResidentProfileView profile={selectedPerson} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {invitePerson && (
        <InviteToCoffeeDialog
          open
          onOpenChange={(open) => {
            if (!open) setInvitePerson(null);
          }}
          recipientUserId={invitePerson.user_id}
        />
      )}
    </div>
  );
}

function FilterGroup({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5 first:mt-2">
      <div className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-td-dark">
        <span className="text-td-muted">{icon}</span>
        {label}
      </div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function PillToggle({
  active,
  children,
  onClick,
}: {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full rounded-xl border px-3 py-2.5 text-left text-sm transition " +
        (active
          ? "border-td-dark bg-td-dark text-white"
          : "border-td-line bg-white text-td-dark hover:border-td-dark/40")
      }
    >
      {children}
    </button>
  );
}
